import { AsyncPipe } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ContactService } from '@customer/contact';
import { Router } from '@angular/router';
import { SpinnerService, StorageService, StrapiService } from '@core/services';
import { Account } from '@legacy-models/portal/Account';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';

type CurrentUser = Parameters<ContactService['switchCurrentAccount']>[1];

@Component({
  selector: 'oke-switcher',
  imports: [ReactiveFormsModule, NgbTypeaheadModule, AsyncPipe],
  templateUrl: './switcher.html',
  styleUrl: './switcher.scss'
})
export class Switcher implements OnInit {
  readonly router = inject(Router);
  readonly spinnerSvc = inject(SpinnerService);
  readonly storageSvc = inject(StorageService);
  private readonly _strapiSvc = inject(StrapiService);
  private readonly _contactSvc = inject(ContactService);
  readonly $accounts = input<Account[]>([], { alias: 'accounts' });
  accounts$: Observable<Account[]>;
  isClicked = false;
  isLoading = false;
  filter = new FormControl('');
  index: number | null = 0;
  acctId: string;
  currentUser: CurrentUser | null = null;
  strapiContentSource = new BehaviorSubject<string>(`
    <h5>Companies you may access</h5>
    <p>Search below and or select a company to see its data.</p>
  `);
  strapiContent$ = this.strapiContentSource.asObservable();

  constructor() {
    this.storageSvc.currentAccount$.subscribe(acct => {
      if (acct) {
        this.acctId = acct.AccountId;
      }
    });
    this.storageSvc.currentUser$.subscribe(user => {
      if (user?.Contact?.Email) {
        this.currentUser = user as CurrentUser;
      }
    });
  }

  ngOnInit(): void {
    this.getContent();
    this.setSwitchAccountsSearcFilter(this.$accounts());
  }

  getContent(): void {
    const strapiContent = JSON.parse(sessionStorage.getItem('strapiComponentSwitchAccounts') as string);
    if (!strapiContent) {
      this._strapiSvc.getSwitchAccountsContent().subscribe(content => {
        sessionStorage.setItem('strapiComponentSwitchAccounts', JSON.stringify(content));
        this.strapiContentSource.next(content);
      });
    } else {
      this.strapiContentSource.next(strapiContent);
    }
  }

  search(text: string, accounts: Account[]): Account[] {
    return accounts.filter(account => {
      account.AccountName = account.AccountName.toLowerCase();
      const term = text.toLowerCase();
      const isMatch = account.AccountName.includes(term);
      account.AccountName = account.AccountName.toUpperCase();

      return isMatch;
    });
  }

  switchAccount(acctId: string, i: number): void {
    if (this.index === i && this.isClicked) {
      this.index = null;
    } else {
      this.isClicked = true;
      this.index = i;
    }

    if (this.currentUser) {
      this._contactSvc.switchCurrentAccount(acctId, this.currentUser);
    }
  }

  setSwitchAccountsSearcFilter(accounts: Account[]): Observable<Account[]> {
    this.accounts$ = this.filter.valueChanges.pipe(
      startWith(''),
      map((text: string | null) => this.search(text ?? '', accounts))
    );

    return this.accounts$;
  }
}
